import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as echarts from 'echarts';
import moment from 'moment';
import { CHART_COLORS } from './chartColors';
import AiChartContextRegistration from '@/containers/facility/AiAssistant/AiChartContextRegistration';

const ClassName_Savings_CumulativeCost = 'ChartsClassName-Savings-CumulativeCost';

export const config = {
    title: 'Cumulative Cost Savings',
    className: ClassName_Savings_CumulativeCost
}

export const CumulativeCostSavingsChart = ({ data }) => {
    // 状态管理隐藏的系列
    const [hiddenSeries, setHiddenSeries] = useState(new Set());
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    // 处理数据，分离电力和燃气数据
    const gasData = data
        .filter(item => item.type === 'Gas')
        .map(item => [item.date, item.value]);
    
    const electricityData = data
        .filter(item => item.type === 'Electricity')
        .map(item => [item.date, item.value]);

    // 创建图表配置
    const createChartOption = useCallback(() => {
        return {
            title: {
                text: config.title,
                left: '1%',
                top: '2%',
                textStyle: {
                    color: CHART_COLORS.title,
                    fontSize: 14
                }
            },

            dataZoom: [],
            animation: true,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'none'
                },
                formatter: function(params) {
                    // 获取日期信息
                    const dateValue = params[0].axisValue || params[0].name || params[0].data[0];
                    const monthHeader = `<div style="margin-bottom: 5px;"><b>Month: ${moment(dateValue).format('MMM YYYY')}</b></div>`;
                    
                    let total = 0;
                    const result = params.map(param => {
                        const value = param.value[1];
                        total += value;
                        const formattedValue = `$${Math.round(value).toLocaleString()}`;
                        const color = param.seriesName === 'Gas' ? CHART_COLORS.gas : CHART_COLORS.electricity;
                        const marker = `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${color};"></span>`;
                        return `${marker}${param.seriesName}: ${formattedValue}`;
                    }).join('<br/>');
                    
                    // 添加总计行
                    const totalLine = `<br/><div style="margin-top:5px;border-top:1px solid #ccc;padding-top:5px;">Total: $${Math.round(total).toLocaleString()}</div>`;
                    return monthHeader + result + totalLine;
                }
            },
            legend: {
                data: ['Gas', 'Electricity'],
                top: '10%',
                itemWidth: 10,
                itemHeight: 10,
                selected: {
                    'Gas': !hiddenSeries.has('Gas'),
                    'Electricity': !hiddenSeries.has('Electricity')
                }
            },
            grid: {
                left: '1%',
                right: '2%',
                bottom: '1%',
                top: '20%',
                containLabel: true
            },
            xAxis: [
                {
                    type: 'category',
                    boundaryGap: true,
                    data: [...new Set(data.map(item => item.date))],
                    axisLabel: {
                        rotate: 50,
                        formatter: function(value) {
                            return moment(value).format('MMM');
                        }
                    },
                    position: 'bottom'
                },
                {
                    type: 'category',
                    position: 'top',
                    boundaryGap: true,
                    axisLine: {
                        show: true
                    },
                    axisTick: {
                        show: false
                    },
                    axisLabel: {
                        formatter: function(value) {
                            return moment(value).format('YYYY');
                        }
                    },
                    data: [...new Set(data.map(item => item.date))]
                }
            ],
            yAxis: {
                type: 'value',
                name: 'Cost ($)',
                nameLocation: 'middle',
                nameGap: 100,
                nameTextStyle: {
                    fontWeight: 'bold',
                    color: CHART_COLORS.axisLabel
                },
                axisLine: {
                    show: true
                },
                axisLabel: {
                    formatter: function(value) {
                        const absValue = Math.abs(value);
                        if (absValue >= 1000) {
                            return `$${value < 0 ? '-' : ''}${Math.round(absValue / 1000)}K`;
                        }
                        return `$${value}`;
                    }
                }
            },
            series: [
                {
                    name: 'Electricity',
                    type: 'line',
                    stack: 'Total',
                    stackStrategy: 'all',
                    data: electricityData,
                    symbolSize: 8,
                    symbol: 'circle',
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: CHART_COLORS.electricity,
                        color: '#fff'
                    },
                    lineStyle: {
                        width: 2,
                        color: CHART_COLORS.electricity
                    },
                    areaStyle: {
                        opacity: 0.8,
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [{
                                offset: 0,
                                color: CHART_COLORS.electricity
                            }, {
                                offset: 1,
                                color: `rgba(${CHART_COLORS.electricityRgb}, 0.2)`
                            }]
                        }
                    }
                },
                {
                    name: 'Gas',
                    type: 'line',
                    stack: 'Total',
                    stackStrategy: 'all',
                    data: gasData,
                    symbolSize: 8,
                    symbol: 'circle',
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: CHART_COLORS.gas,
                        color: '#fff'
                    },
                    lineStyle: {
                        width: 2,
                        color: CHART_COLORS.gas
                    },
                    areaStyle: {
                        opacity: 0.8,
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [{
                                offset: 0,
                                color: CHART_COLORS.gas
                            }, {
                                offset: 1,
                                color: `rgba(${CHART_COLORS.gasRgb}, 0.2)`
                            }]
                        }
                    }
                }
            ]
        };
    }, [data, gasData, electricityData, hiddenSeries]);

    // 初始化图表
    useEffect(() => {
        if (chartRef.current && !chartInstanceRef.current) {
            const chart = echarts.init(chartRef.current);
            chartInstanceRef.current = chart;
            
            // 设置图表配置
            chart.setOption(createChartOption());
            
            // 监听 legend 点击事件
            chart.on('legendselectchanged', (params) => {
                const seriesName = params.name;
                setHiddenSeries(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(seriesName)) {
                        newSet.delete(seriesName);
                    } else {
                        newSet.add(seriesName);
                    }
                    return newSet;
                });
            });

            // 监听窗口大小变化
            const handleResize = () => {
                chart.resize();
            };
            window.addEventListener('resize', handleResize);

            // 清理函数
            return () => {
                window.removeEventListener('resize', handleResize);
                chart.dispose();
                chartInstanceRef.current = null;
            };
        }
    }, []);

    // 当数据或隐藏状态改变时，更新图表
    useEffect(() => {
        if (chartInstanceRef.current) {
            const chart = chartInstanceRef.current;
            chart.setOption(createChartOption(), true);
        }
    }, [createChartOption]);

    return (
        <div className="facility-ai-chart-frame" style={{ height: "100%", width: "100%" }}>
            <AiChartContextRegistration
                context={{
                    pageId: "page.facility.savings",
                    chart: {
                        chartId: "savings.combined.cumulative_cost",
                        title: config.title,
                        aliases: ["cumulative savings", "cumulative cost chart", "累计节省", "累计成本节省图"],
                        chartType: "stacked_area",
                        description: "Running cumulative cost savings split between electricity and gas.",
                        sourceOperationIds: ["savings.cumulative_cost"],
                        series: [
                            {
                                name: "Electricity",
                                unit: "USD",
                                visible: !hiddenSeries.has("Electricity"),
                                points: electricityData.map(([date, value]) => ({ label: date, value })),
                            },
                            {
                                name: "Gas",
                                unit: "USD",
                                visible: !hiddenSeries.has("Gas"),
                                points: gasData.map(([date, value]) => ({ label: date, value })),
                            },
                        ],
                        completeness: data.length ? "complete" : "unknown",
                        limitations: ["Series are stacked in the UI; the total is the sum of visible utility series."],
                    },
                }}
            />
            <div
                ref={chartRef}
                className={config.className}
                style={{ flex: '1 1 0', minHeight: 0, width: '100%' }}
            />
        </div>
    );
}
